import { vec3, vec4 } from 'gl-matrix';
import { Scene } from '../scene/Scene';
import { Mesh, GltfMesh } from '../mesh';
import { RenderObject } from './RenderObject';
import { RenderConfiguration } from '../renderer';
import { Texture } from '../texture';
import { Material } from '../material';
import { ResolvedMesh, ResolvedPrimitive } from '../gltf';

export type InstantiateMeshOptions = {
  position?: vec3 | number[];
  scale?: number;
  /** Wrapped in a fresh Material so the obj renders with a flat color. */
  color?: vec4;
  /** Set as the material's baseColor texture. */
  texture?: Texture;
  /**
   * Override the default culling mode. Must be applied before the object
   * reaches ObjectManager.addObject — once registered, switching modes would
   * leave the previous index out of sync.
   */
  culling?: RenderConfiguration['culling'];
  /**
   * Skip the automatic ObjectManager.addObject call. The caller becomes
   * responsible for calling `scene.objectManager.addObject(obj)` after any
   * post-construction TRS edits. Required for octree-mode objects whose
   * world position is set after instantiation: registering at default
   * origin-bounds would land them in a stale leaf, so the octree's spatial
   * structure no longer matches the actual placement.
   */
  deferRegister?: boolean;
};

export type InstantiateModelOptions = {
  meshName?: string;
  primitiveIndex?: number;
  position?: vec3 | number[];
  scale?: number;
};

/**
 * Central entry point for creating RenderObjects.
 *
 * Every code path that produces a renderable object should funnel through here
 * so registration with ObjectManager (and any future bookkeeping) lives in one
 * place. Use {@link instantiateMesh} for raw meshes you have already built,
 * {@link instantiateModel} to spawn from the scene's parsed-model registry,
 * and {@link instantiateResolvedMesh} for ad-hoc gltf parsing flows (e.g. the
 * asset manager) that produced a {@link ResolvedMesh} outside the registry.
 */
export class RenderObjectFactory {
  constructor(private scene: Scene) {}

  public instantiateMesh(mesh: Mesh, opts?: InstantiateMeshOptions): RenderObject {
    const obj = mesh.createInstance();
    if (opts) this.applyOptions(obj, opts);
    if (!opts?.deferRegister) {
      this.scene.objectManager.addObject(obj);
    }
    return obj;
  }

  public instantiateModel(
    modelName: string,
    opts?: InstantiateModelOptions,
  ): RenderObject | null {
    const model = this.scene.modelRegistry.get(modelName);
    if (!model) return null;

    const meshName = opts?.meshName ?? model.meshes[0]?.name;
    const primitiveIndex = opts?.primitiveIndex ?? 0;

    const resolved = meshName ? model.meshByName.get(meshName) : undefined;
    if (!resolved || resolved.primitives.length <= primitiveIndex) return null;

    // _MODEL_TRANSFORM and _MESH_TRANSFORM are baked into the vertices held by
    // the model registry (via resolveSceneMeshes), so no extra obj.trs
    // composition is needed here.
    return this.instantiatePrimitive(
      resolved.primitives[primitiveIndex],
      model.materials,
      `${modelName}:${meshName}:${primitiveIndex}`,
      opts,
    );
  }

  /**
   * Build a RenderObject for every primitive in a {@link ResolvedMesh}, applying
   * materials by index. Used by ad-hoc gltf parsing flows that don't go
   * through {@link Scene.modelRegistry}.
   */
  public instantiateResolvedMesh(
    resolved: ResolvedMesh,
    materials: Map<number, Material>,
    namePrefix: string,
  ): RenderObject[] {
    const objects: RenderObject[] = [];
    for (let j = 0; j < resolved.primitives.length; j++) {
      objects.push(
        this.instantiatePrimitive(resolved.primitives[j], materials, `${namePrefix}_${j}`),
      );
    }
    return objects;
  }

  private instantiatePrimitive(
    prim: ResolvedPrimitive,
    materials: Map<number, Material>,
    name: string,
    opts?: InstantiateMeshOptions,
  ): RenderObject {
    const mesh = new GltfMesh(name, this.scene.ctx, this.scene, prim);
    const obj = this.instantiateMesh(mesh, opts);
    if (prim.materialIndex !== undefined) {
      const mat = materials.get(prim.materialIndex);
      if (mat) obj.material = mat;
    }
    return obj;
  }

  private applyOptions(obj: RenderObject, opts: InstantiateMeshOptions): void {
    if (opts.position) {
      obj.trs.position[0] = opts.position[0] as number;
      obj.trs.position[1] = opts.position[1] as number;
      obj.trs.position[2] = opts.position[2] as number;
    }
    if (opts.scale !== undefined) {
      obj.trs.scale = opts.scale;
    }
    if (opts.texture || opts.color) {
      const material = new Material();
      if (opts.texture) material.baseColor = opts.texture;
      if (opts.color) material.color = opts.color;
      obj.material = material;
    }
    if (opts.culling !== undefined) {
      obj.renderConfiguration.culling = opts.culling;
    }
  }
}
